import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../utils/axiosSetup.js';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import { closeModal } from '../../../action/modalbehaviour.js';


const AddProjectMeter = ({list}) => {
    const [channels, setChannel] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [meters, setMeters] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [meterType, setMeterType] = useState('1');

    const schema = Yup.object().shape({
        channel_id: Yup.string().required('channel is a required field'),
        client_id: Yup.string().required('client is a required field'),
        project_id: Yup.string().required('project is a required field'),
        meter_type: Yup.string().required('meter type is a required field'),
        meter_id: Yup.string().required('meter is a required field'),
        price: Yup.number().required('price is a required field').typeError('price must be a number')
    });
    
    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(schema)
    });

    const onSubmit = async (data) => {
        axiosInstance.post(`${process.env.REACT_APP_API_URL}/project_meter`, data)
            .then(response => {
                Swal.fire({icon:"success", text:response.data.message});
                reset();
                closeModal("add_project_meter");
                list();
            })
            .catch(({response:{data}})=>{
                Swal.fire({text:data.message, icon:"error"});
                console.error('Error creating project meter', data);
            });
    }

    const fetchChannels = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/channels`)
            .then(response => {
                setChannel(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching channels', error);
                setChannel([]);
            });
    };

    const fetchClients = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/clients`)
            .then(response => {
                setClients(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching clients', error);
                setClients([]);
            });
    }

    const fetchProjects = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/projects`)
            .then(response => {
                setProjects(response.data.data);
            })
            .catch(error => {
                console.error("Error fetching projects!", error);
                setProjects([]);
            });
    };

    const fetchMeters = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/meter`)
            .then(response => {
                setMeters(response.data.data);
            })
            .catch(error => {
                console.error("Error fetching meters!", error);
                setMeters([]);
            });
    };

    useEffect(() => {
        fetchChannels();
        fetchClients();
        fetchProjects();
        fetchMeters();

        const modalElement = document.getElementById('add_project_meter');

        const handleClose = () => {
            reset();
            setSelectedChannel(null);
            setSelectedClient(null);
            setMeterType('1');
        };

        if (modalElement) {
            modalElement.addEventListener('hidden.bs.modal', handleClose);
        }
        return () => {
            if (modalElement) {
                modalElement.removeEventListener('hidden.bs.modal', handleClose);
            }
        };
    }, [reset]);

    // Filter clients based on selected channel
    const filteredClients = selectedChannel 
        ? clients.filter(client => parseInt(client.channel_id) === parseInt(selectedChannel)) 
        : [];

    // Filter projects based on selected client
    const filteredProjects = selectedClient 
        ? projects.filter(project => parseInt(project.client_id) === parseInt(selectedClient)) 
        : [];

    const handleMeterTypeChange = (event) => {
        setMeterType(event.target.value);
    };
    
    return (
        <div id="add_project_meter" className="modal custom-modal fade" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Project Meter</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Channel <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('channel_id')}
                                                onChange={(event) => {setSelectedChannel(event.target.value)}}>
                                            <option value="">Select Channel</option>
                                            {channels.filter(channel => channel.name === 'Upwork').map(channel => (
                                                <option key={channel.id} value={channel.id}>{channel.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.channel_id && <p>{errors.channel_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Client <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('client_id')}
                                                onChange={(event) => {setSelectedClient(event.target.value)}}>
                                            <option value="">Select Client</option>
                                            {filteredClients.map(client => (
                                                <option key={client.id} value={client.id}>{client.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.client_id && <p>{errors.client_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Project <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('project_id')}>
                                            <option value="">Select Project</option>
                                            {filteredProjects.map(project => (
                                                <option key={project.id} value={project.id}>{project.title}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.project_id && <p>{errors.project_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Meter Type <span className="text-danger">*</span></label>
                                        <div className="radio">
                                            <label className="col-form-label" style={{marginRight:'15px'}}>
                                                <input type="radio" value="1" {...register('meter_type')} onChange={handleMeterTypeChange} /> Hourly
                                            </label>
                                            <label className="col-form-label" style={{marginRight:'15px'}}>
                                                <input type="radio" value="0" {...register('meter_type')} onChange={handleMeterTypeChange} /> Fixed
                                            </label>
                                        </div>
                                        <div className="text-sm text-danger">
                                            {errors.meter_type && <p>{errors.meter_type.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Meter <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('meter_id')}>
                                            <option value="">Select Meter</option>
                                            {meters.map(meter => (
                                                <option key={meter.id} value={meter.id}>{meter.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.meter_id && <p>{errors.meter_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">{meterType === '1' ? 'Hourly' : 'Fixed'} Price 
                                            (<i className="fa-solid fa-dollar-sign"></i>) <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" {...register('price')}/>
                                        <div className="text-sm text-danger">
                                            {errors.price && <p>{errors.price.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Description</label>
                                        <textarea className="form-control" type="text" {...register('description')}/>
                                    </div>
                                </div>
                            </div>
                            <div className="submit-section">
                                <button type="submit" className="btn btn-primary submit-btn">SUBMIT</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddProjectMeter